import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';
import { useModal } from '../components/useModal';

/**
 * Subscription detail — a membership someone is paying for.
 *
 * All twenty-two fields, all five links, and all six actions with the exact
 * conditions the old page uses to enable them:
 *
 *   Regret            `isRegrettable` — a separate call, because it is a rule
 *                     about time and refunds the payload does not carry
 *   Cancel, Suspend   Active
 *   Charge            Active
 *   Resume            Suspended, and not already scheduled to resume
 *   Force termination anything but Terminated
 *
 * The old page guards all six with one sentence — "Are you sure you want to
 * proceed?" — and adds a warning only for force termination. Six actions that
 * differ in whether the user gets their money back deserve six different
 * sentences, so each dialog says what its own action does. Force termination
 * keeps the warning it had, which is the important one: no refund.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');
const fmtDay = value => (value ? String(value).split('T')[0] : null);

const range = (from, to) => {
  const a = fmtDay(from);
  const b = fmtDay(to);
  if (!a && !b) return null;
  return `${a || '…'} → ${b || '…'}`;
};

const price = value => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') {
    const amount = value.Amount ?? value.amount;
    const currency = value.Currency ?? value.currency ?? 'DKK';
    return amount === null || amount === undefined ? null : `${Number(amount).toFixed(2)} ${currency}`;
  }
  return `${Number(value).toFixed(2)} DKK`;
};

const SubscriptionInternal = ({ api }) => {
  const { subscriptionId } = useParams();
  const { ask, modalProps } = useModal();

  const [subscription, setSubscription] = useState(null);
  const [regrettable, setRegrettable] = useState(false);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.subscriptions.getSubscription(subscriptionId);
      if (!data || data.success === false) {
        setFailed(true);
        return;
      }
      setSubscription(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }

    try {
      // Its own endpoint: whether a regret is still possible is a rule about
      // when the subscription started, not a field on it.
      const answer = await api.subscriptions.isRegrettable(subscriptionId);
      setRegrettable(answer === true || answer?.IsRegrettable === true || answer?.isRegrettable === true);
    } catch (error) {
      // Not knowing means the action stays closed rather than offering something
      // that would fail at the provider.
      setRegrettable(false);
    }
  }, [api, subscriptionId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = (fn, okText, failText) => async () => {
    try {
      await fn();
      setNote({ tone: 'info', text: okText });
    } catch (error) {
      setNote({ tone: 'error', text: `${failText}: ${error?.message || 'unknown error'}` });
      await load();
      throw error;
    }
    await load();
  };

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/subscriptions">← All subscriptions</BackToList>
        <div className="next-note next-note--error">
          Could not load this subscription.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!subscription) return <div className="next-page next-muted">Loading…</div>;

  const status = subscription.Status;
  const ctx = `subscription …${String(subscriptionId).slice(-6)}`;
  const amount = price(subscription.Price);
  const methodType = subscription.PaymentMethod?.Type || 'creditcard';
  const seasonal = subscription.MembershipLogicType === 'SeasonalSlots'
    || subscription.MembershipLogicType === 'SeasonalSlotsPreSales';

  const actions = [
    {
      label: 'Regret',
      enabled: regrettable,
      why: 'The regret window has passed, or the provider says this one cannot be regretted.',
      spec: {
        kind: 'confirm',
        title: 'Regret this subscription?',
        body: `The subscription ends and the user is refunded${amount ? ` ${amount}` : ''}. This is the option that gives the money back — prefer it over force termination whenever it is available.`,
        confirmLabel: 'Regret',
        onConfirm: run(() => api.subscriptions.regretSubscription(subscriptionId),
          'Regretted.', 'Unable to regret')
      }
    },
    {
      label: 'Cancel',
      enabled: status === 'Active',
      why: 'Only an active subscription can be cancelled.',
      spec: {
        kind: 'confirm',
        title: 'Cancel this subscription?',
        body: 'It stops renewing at the end of the current billing period. The user keeps what they have already paid for until then.',
        confirmLabel: 'Cancel subscription',
        onConfirm: run(() => api.subscriptions.cancelSubscription(subscriptionId),
          'Cancelled.', 'Unable to cancel')
      }
    },
    {
      label: 'Suspend',
      enabled: status === 'Active',
      why: 'Only an active subscription can be suspended.',
      spec: {
        kind: 'confirm',
        title: 'Suspend this subscription?',
        body: 'Billing pauses and the membership stops giving access until it is resumed.',
        confirmLabel: 'Suspend',
        onConfirm: run(() => api.subscriptions.suspendSubscription(subscriptionId),
          'Suspended.', 'Unable to suspend')
      }
    },
    {
      label: 'Resume',
      enabled: status === 'Suspended' && !subscription.SuspendedTo,
      why: subscription.SuspendedTo
        ? `This one already resumes by itself on ${fmtDay(subscription.SuspendedTo)}.`
        : 'Only a suspended subscription can be resumed.',
      spec: {
        kind: 'confirm',
        title: 'Resume this subscription?',
        body: 'Billing starts again and the membership gives access from now.',
        confirmLabel: 'Resume',
        onConfirm: run(() => api.subscriptions.resumeSubscription(subscriptionId),
          'Resumed.', 'Unable to resume')
      }
    },
    {
      label: 'Charge',
      enabled: status === 'Active',
      why: 'Only an active subscription can be charged.',
      spec: {
        kind: 'confirm',
        title: 'Charge this subscription now?',
        body: `A payment${amount ? ` of ${amount}` : ''} is taken from the payment method on file, outside the normal billing cycle.`,
        warning: 'The user is charged immediately — this is not a dry run.',
        confirmLabel: 'Charge now',
        onConfirm: run(() => api.subscriptions.charge(subscriptionId),
          'Charged.', 'Unable to charge')
      }
    },
    {
      label: 'Force termination',
      tone: 'danger',
      enabled: status !== 'Terminated',
      why: 'It is already terminated.',
      spec: {
        kind: 'danger',
        title: 'Force this subscription to terminate?',
        body: 'The subscription ends immediately, whatever its billing period says.',
        warning: 'The user gets NO refund. Use Regret instead whenever it is available — that is the one that returns the money.',
        confirmLabel: 'Force termination',
        onConfirm: run(() => api.subscriptions.forceTermination(subscriptionId),
          'Terminated.', 'Unable to terminate')
      }
    }
  ];

  const plan = Array.isArray(subscription.PaymentPlan) ? subscription.PaymentPlan : [];

  const facts = [
    ['Status', status ? <StatusPill status={status}>{status}</StatusPill> : <span className="next-muted">—</span>],
    ['Price', amount || <span className="next-muted">—</span>],
    ['Text', subscription.Text || <span className="next-muted">—</span>],
    ['Reference', subscription.Reference || <span className="next-muted">—</span>],
    ['External reference', subscription.ExternalReference || <span className="next-muted">—</span>],
    ['User id', subscription.UserId
      ? <Guid value={subscription.UserId} to={`/next/user/${subscription.UserId}`} />
      : <span className="next-muted">—</span>],
    ['Membership id', subscription.MembershipId
      ? <Guid value={subscription.MembershipId} href={`/product/details/${subscription.MembershipId}`} />
      : <span className="next-muted">—</span>],
    ['Payment method', subscription.PaymentMethodId
      ? <Guid value={subscription.PaymentMethodId} href={`/paymentmethod/${methodType}/${subscription.PaymentMethodId}`} />
      : <span className="next-muted">—</span>],
    ['Preferred club', subscription.PreferredClub
      ? <Guid value={subscription.PreferredClub} href={`/facility/${subscription.PreferredClub}`} />
      : <span className="next-muted">—</span>],
    ['Created at (CET)', fmtDate(subscription.CreatedAtCet)],
    ['Updated at', fmtDate(subscription.UpdatedAt)],
    ['Next billing at', fmtDate(subscription.NextBillingAt)],
    ['Billing period', range(subscription.BillingPeriodStartsAt, subscription.BillingPeriodEndsAt) || '—'],
    ['Termination period', range(subscription.TerminationPeriodStartsAt, subscription.TerminationPeriodEndsAt) || '—'],
    ['Bucket period', range(subscription.BucketPeriodStartsAt, subscription.BucketPeriodEndsAt) || '—'],
    ['Paid through at', fmtDate(subscription.PaidThroughAt)],
    ['Terminated at', fmtDate(subscription.TerminatedAt)],
    ['Discount expires at', fmtDate(subscription.DiscountExpireAt)],
    ['Suspended from', fmtDate(subscription.SuspendedFrom)],
    ['Suspended to', fmtDate(subscription.SuspendedTo)],
    ['Last regret at', fmtDate(subscription.LastRegretAt)]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/subscriptions">← All subscriptions</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{subscription.Text || 'Subscription'}</h1>
          {status && <StatusPill status={status}>{status}</StatusPill>}
        </div>
        <span className="next-detail__id">{subscription.Id || subscriptionId}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Subscription</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          {plan.length > 0 && (
            <section className="next-card">
              <div className="next-card__head">
                <h2>Payment plan</h2>
                <span className="next-muted">{plan.length} period{plan.length === 1 ? '' : 's'}</span>
              </div>
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="next-table__right">Discount</th>
                      <th className="next-table__right">Fixed amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.map((row, index) => (
                      <tr key={`${row.BillingPeriodStart}-${index}`}>
                        <td data-label="" className="next-nowrap">
                          {range(row.BillingPeriodStart, row.BillingPeriodEnd) || '—'}
                        </td>
                        {/* "n.d." in the old page — an empty cell says the same
                            thing without looking like a value. */}
                        <td data-label="Discount" className="next-table__right">
                          {row.Discount ?? <span className="next-muted">—</span>}
                        </td>
                        <td data-label="Fixed amount" className="next-table__right">
                          {row.FixedAmount ?? <span className="next-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              {actions.map(({ label, enabled, why, tone, spec }) => (
                <button
                  type="button"
                  key={label}
                  className={`next-actionlist__item${tone === 'danger' ? ' next-actionlist__item--danger' : ''}`}
                  disabled={!enabled}
                  title={enabled ? undefined : why}
                  onClick={() => ask({ context: `${label} · ${ctx}`, ...spec })}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                subscription.UserId && {
                  label: 'User',
                  to: `/next/user/${subscription.UserId}`
                },
                {
                  label: 'Transactions',
                  hint: 'billed on this subscription',
                  to: `/next/transactions?subscriptionId=${subscription.Id || subscriptionId}`
                },
                subscription.PaymentMethodId && {
                  label: 'Payment method',
                  href: `/paymentmethod/${methodType}/${subscription.PaymentMethodId}`
                },
                subscription.MembershipId && {
                  label: 'Membership',
                  href: `/product/details/${subscription.MembershipId}`
                },
                // Only for the two membership types that hold seasonal slots —
                // the same condition the old page uses.
                seasonal && {
                  label: 'Seasonal slot purchases',
                  href: `/seasonalcourtpurchases?subscriptionId=${subscription.Id || subscriptionId}`
                },
                subscription.UserId && {
                  label: 'This user’s subscriptions',
                  to: `/next/subscriptions?userId=${subscription.UserId}`
                }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

SubscriptionInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Subscription = withApi(SubscriptionInternal);

Subscription.requiresLogin = true;

export default Subscription;
